import { Box, Typography, useTheme } from '@mui/material'
import React, { memo, useMemo } from 'react'
import { TradingSession } from '../../proto/generated/trading-calendar'
import { formatEpochTime } from '../../util/dateTime'
import { marketStatusBlue, marketStatusGreen, marketStatusOrange } from '../../assets/colors'
import { NonInteractiveTooltip } from '../../components/custom'

const DAY_MINUTES = 24 * 60
const BAR_HEIGHT = 7

export interface MarketStatusBarProps {
    tradingSession: TradingSession
}

const minutesOfDay = (epoch: number) => {
    const date = new Date(epoch)
    return date.getHours() * 60 + date.getMinutes()
}

const toPercent = (epoch: number) => (minutesOfDay(epoch) / DAY_MINUTES) * 100

const weekDay = (epoch: number) => {
    const days = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']
    return days[new Date(epoch).getDay()].toUpperCase()
}

interface SegmentProps {
    color: string
    left: number
    width: number
    text: string
}

const Segment = memo(({ color, left, width, text }: SegmentProps) => {
    const tooltipContent = useMemo(() => (
        <Typography variant="caption">{text}</Typography>
    ), [text])

    if (width <= 0) return null

    return (
        <NonInteractiveTooltip title={tooltipContent} arrow={true} placement="top">
            <div style={{
                position: 'absolute',
                left: `${left}%`,
                width: `${width}%`,
                minWidth: '4px',
                backgroundColor: color,
                height: `${BAR_HEIGHT}px`,
                borderRadius: 0
            }} />
        </NonInteractiveTooltip>
    )
})

Segment.displayName = 'Segment'

const MarketStatusBar = (props: MarketStatusBarProps) => {
    const theme = useTheme()
    const tradingSession = props.tradingSession

    const preLeft = toPercent(tradingSession.preOpenStartTime)
    const preRight = toPercent(tradingSession.preOpenEndTime)
    const openLeft = toPercent(tradingSession.startTime)
    const openRight = toPercent(tradingSession.endTime)
    const postLeft = toPercent(tradingSession.postCloseStartTime)
    const postRight = toPercent(tradingSession.postCloseEndTime)

    const now = new Date()
    const nowPercent = ((now.getHours() * 60 + now.getMinutes()) / DAY_MINUTES) * 100

    return (
        <Box sx={{ width: '100%', display: 'flex', alignItems: 'flex-start', gap: '8px', marginTop: '6px' }}>
            <Typography variant={'caption'}
                        sx={{ lineHeight: `${BAR_HEIGHT}px`, color: 'text.secondary' }}>{weekDay(tradingSession.startTime)}</Typography>
            <Box sx={{ flex: 1 }}>
                <Box sx={{ position: 'relative', width: '100%', height: `${BAR_HEIGHT}px` }}>
                    <div style={{
                        position: 'absolute',
                        left: 0,
                        right: 0,
                        top: 0,
                        height: `${BAR_HEIGHT}px`,
                        borderRadius: '4px',
                        backgroundColor: theme.palette.divider
                    }} />
                    <Segment color={marketStatusOrange} left={preLeft} width={preRight - preLeft}
                             text={`Pre-market ${formatEpochTime(tradingSession.preOpenStartTime)} - ${formatEpochTime(tradingSession.preOpenEndTime)}`} />
                    <Segment color={marketStatusGreen} left={openLeft} width={openRight - openLeft}
                             text={`Market open ${formatEpochTime(tradingSession.startTime)} - ${formatEpochTime(tradingSession.endTime)}`} />
                    <Segment color={marketStatusBlue} left={postLeft} width={postRight - postLeft}
                             text={`Post-market ${formatEpochTime(tradingSession.postCloseStartTime)} - ${formatEpochTime(tradingSession.postCloseEndTime)}`} />
                    <div style={{
                        position: 'absolute',
                        left: `${nowPercent}%`,
                        top: '-3px',
                        width: '1px',
                        height: `${BAR_HEIGHT + 6}px`,
                        borderRadius: '1px',
                        backgroundColor: theme.palette.text.primary
                    }} />
                </Box>
                <Box sx={{ position: 'relative', height: '16px', marginTop: '4px' }}>
                    <Typography variant={'caption'}
                                sx={{ position: 'absolute', left: 0, transform: 'translateX(-50%)', color: 'text.tertiary' }}>00:00</Typography>
                    <Typography variant={'caption'}
                                sx={{ position: 'absolute', left: '100%', transform: 'translateX(-50%)', color: 'text.tertiary' }}>24:00</Typography>
                </Box>
            </Box>
        </Box>
    )
}


export default memo(MarketStatusBar)
